import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Rocket, Save } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { useDraftStore } from "@/shared/store/draft";
import { useSaveDraft } from "@/shared/api/wb";
import { Toast } from "@/shared/ui";

const BASE_WIZARD_STEPS = [
  { path: "/campaign/new/product",   label: "Продукт" },
  { path: "/campaign/new/bloggers",  label: "Блогеры" },
  { path: "/campaign/new/creative",  label: "Креатив" },
  { path: "/campaign/new/review",    label: "Обзор" },
  { path: "/campaign/new/payment",   label: "Оплата" },
];

// Budget step is intentionally NOT in this list — the dedicated step was
// removed; budget is now picked inside a popover that opens from the Bloggers
// step via the "Как определить бюджет?" link. The BudgetStep component and
// the /campaign/new/budget route are kept for future re-introduction or
// for direct URL access.
export const WIZARD_STEPS = BASE_WIZARD_STEPS;

export function WizardStepper() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const campaignType = useDraftStore((s) => s.branch.campaignType);
  const steps = BASE_WIZARD_STEPS.map((s) => {
    if (s.path === "/campaign/new/bloggers")
      return { ...s, label: campaignType === "seeding" ? "Каналы" : "Блогеры" };
    if (s.path === "/campaign/new/creative")
      return { ...s, label: campaignType === "seeding" ? "Креатив" : "ТЗ" };
    return s;
  });
  const activeIdx = steps.findIndex((s) => pathname.startsWith(s.path));
  // Scroll to top whenever the wizard step changes.
  useEffect(() => {
    if (activeIdx < 0) return;
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    document.querySelector("main")?.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [activeIdx, pathname]);
  if (activeIdx < 0) return null;

  return (
    <div className="sticky top-0 z-20 bg-[var(--bg)] py-3 px-7 border-b border-ink-12 flex items-center gap-4">
      <div className="flex items-center gap-1 flex-1 overflow-hidden">
        {steps.map((s, i) => {
          const state = i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
          const clickable = i <= activeIdx;
          return (
            <div key={s.path} className="flex items-center gap-1 min-w-0">
              {i > 0 && <span className="w-3 h-px bg-ink-12 flex-shrink-0" />}
              <button
                disabled={!clickable}
                onClick={() => navigate(s.path)}
                className={cn(
                  "flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-pill text-[13px] font-display font-semibold transition-colors whitespace-nowrap",
                  state === "active" && "bg-ink text-white",
                  state === "done"   && "text-ink-60 hover:text-ink hover:bg-ink-6 cursor-pointer",
                  state === "pending" && "text-ink-40 cursor-not-allowed",
                )}
              >
                <span
                  className={cn(
                    "w-[22px] h-[22px] grid place-items-center rounded-full font-mono text-[11px] flex-shrink-0",
                    state === "active"  && "bg-lime text-[var(--fg-on-accent)]",
                    state === "done"    && "bg-ink text-white",
                    state === "pending" && "bg-ink-6 text-ink-60",
                  )}
                >
                  {state === "done" ? <Check size={11} strokeWidth={3} /> : i + 1}
                </span>
                {s.label}
              </button>
            </div>
          );
        })}
      </div>

      <SaveDraftButton currentPath={steps[activeIdx].path} />
    </div>
  );
}

/**
 * Persists the wizard draft to the backend. First click creates a new draft
 * Campaign (returned id stored in the draft store); subsequent clicks update
 * the same campaign. The button briefly shows "Сохранено ✓" after success.
 */
function SaveDraftButton({ currentPath }: { currentPath: string }) {
  const { draft, branch, draftCampaignId, setDraftCampaignId } = useDraftStore();
  const saveDraft = useSaveDraft();
  const navigate = useNavigate();
  const [savedTick, setSavedTick] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const onClick = async () => {
    const platforms = Array.from(new Set(
      // Don't have detailed per-blogger platform mapping in the draft store —
      // leave platforms empty; backend will fill it during launch.
      [] as string[],
    ));
    try {
      const res = await saveDraft.mutateAsync({
        campaign_id: draftCampaignId,
        wizard_step: currentPath,
        wizard_draft: { draft, branch },
        product: draft.productName || undefined,
        product_desc: draft.productDesc || undefined,
        product_url: draft.productUrl || undefined,
        product_category: draft.productCategory || undefined,
        product_type: draft.productType || undefined,
        type: branch.campaignType,
        budget: draft.budget,
        geo: draft.geo,
        selected_blogger_ids: draft.selectedBloggerIds,
        platforms,
      });
      setDraftCampaignId(res.id);
      setSavedTick((n) => n + 1);
      setShowToast(true);
    } catch (e) {
      console.error("save draft failed", e);
    }
  };

  useEffect(() => {
    if (!savedTick) return;
    const t = setTimeout(() => setSavedTick(0), 1800);
    return () => clearTimeout(t);
  }, [savedTick]);

  const showSaved = savedTick > 0;
  const showSaving = saveDraft.isPending;

  return (
    <>
      <button
        onClick={onClick}
        disabled={showSaving}
        className={cn(
          "inline-flex items-center gap-1.5 h-9 px-3 rounded-md border-2 text-[12.5px] font-display font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait",
          showSaved
            ? "border-lime bg-lime/10 text-ink"
            : "border-ink-12 text-ink-60 hover:text-ink hover:border-ink/40",
        )}
      >
        {showSaved ? <Check size={14} strokeWidth={3} /> : <Save size={14} />}
        {showSaved ? "Сохранено" : showSaving ? "Сохраняем…" : "Сохранить черновик"}
      </button>
      {showToast && (
        <Toast
          message="Черновик сохранён"
          onHide={() => setShowToast(false)}
          action={{ label: "Открыть в «Мои РК»", onClick: () => navigate("/account/projects") }}
          bottomOffsetPx={88}
        />
      )}
    </>
  );
}

type WizardFooterCtx = { onModerate?: () => void };
const WizardFooterStateKey = "__lk2_wizard_moderate";

export function setWizardModerateHandler(fn: () => void) {
  (window as unknown as Record<string, unknown>)[WizardFooterStateKey] = fn;
}

export function WizardFooterBar() {
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const selectedBloggerIds = useDraftStore((s) => s.draft.selectedBloggerIds);
  const placementDates = useDraftStore((s) => s.draft.placementDates);
  const selectedCount = selectedBloggerIds.length;
  const idx = WIZARD_STEPS.findIndex((s) => pathname.startsWith(s.path));
  if (idx < 0) return null;
  const next = WIZARD_STEPS[idx + 1];
  const prev = WIZARD_STEPS[idx - 1];
  const onPayment = pathname.startsWith("/campaign/new/payment");
  const onReview = pathname.startsWith("/campaign/new/review");
  const returnTo = (locationState as { returnTo?: string } | null)?.returnTo;
  const needsBloggers = (onReview || onPayment) && selectedCount === 0;
  const needsDates = (onReview || onPayment) && selectedCount > 0
    && selectedBloggerIds.some((id) => !placementDates[id]?.date);

  const handleNext = () => {
    const handler = (window as unknown as Record<string, unknown>)[WizardFooterStateKey];
    if (typeof handler === "function") {
      (handler as () => void)();
      return;
    }
    if (returnTo) { navigate(returnTo); return; }
    if (next) navigate(next.path);
    else navigate("/account/projects");
  };

  return (
    <div className="sticky bottom-0 z-20 px-7 py-3.5 border-t border-ink-12 bg-[var(--bg)] flex items-center justify-between">
      <button
        disabled={!prev}
        onClick={() => prev && navigate(prev.path)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill text-[13px] font-display font-semibold text-ink-60 hover:text-ink hover:bg-ink-6 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ArrowLeft size={14} /> Назад
      </button>
      <div className="text-[12.5px] text-ink-40">
        {needsBloggers
          ? <span className="text-warning font-semibold">Добавьте хотя бы один канал</span>
          : needsDates
          ? <span className="text-warning font-semibold">Укажите дату размещения для всех каналов</span>
          : `Шаг ${idx + 1} из ${WIZARD_STEPS.length}`}
      </div>
      <button
        disabled={needsBloggers || needsDates}
        onClick={handleNext}
        className="inline-flex items-center gap-1.5 h-10 px-5 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-lime"
      >
        {onPayment ? "Оплатить и запустить" : "Дальше"}
        {onPayment ? <Rocket size={16} /> : <ArrowRight size={16} strokeWidth={2.5} />}
      </button>
    </div>
  );
}
